/**
 * @fileoverview Display helpers for the profile modal.
 * @description Pure formatting helpers for the signed-in user's profile: avatar initials from their
 * display name, a Badge variant for their account status, and a human label for a banking-role
 * user's data scope. Kept free of React so each is unit-testable on its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { BankingScope } from '@/features/auth/services/session.service'
import type { TFunction } from 'i18next'

type StatusBadgeVariant = 'neutral' | 'success' | 'warning' | 'danger'

const STATUS_BADGE_VARIANTS: Record<string, StatusBadgeVariant> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  REGISTERED: 'warning',
  INACTIVE: 'neutral',
  LOCKED: 'danger',
  SUSPENDED: 'danger',
  DEACTIVATED: 'danger',
}

/**
 * @description Builds up-to-two-letter avatar initials from a display name: the first letter of the
 * first and last words ("Anand Kulkarni" -> "AK", "Anand R. Kulkarni" -> "AK"), or the first two
 * letters of a single word ("Anand" -> "AN").
 * @param {string} displayName - The user's display name (first, optional middle and last name).
 * @returns {string} Uppercase initials, or an empty string when the name is blank.
 */
export function initialsFromName(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean)
  const first = words[0]
  const last = words[words.length - 1]

  if (!first || !last) {
    return ''
  }

  const initials = words.length > 1 ? `${first[0] ?? ''}${last[0] ?? ''}` : first.slice(0, 2)
  return initials.toUpperCase()
}

/**
 * @description Maps an account status to the Badge color that conveys it: ACTIVE green, pending
 * states (INVITED, REGISTERED) amber, blocked states (LOCKED, SUSPENDED, DEACTIVATED) red, anything
 * else neutral.
 * @param {string} status - The identity backend's user-status value.
 * @returns {StatusBadgeVariant} The Badge variant to render.
 */
export function statusBadgeVariant(status: string): StatusBadgeVariant {
  return STATUS_BADGE_VARIANTS[status] ?? 'neutral'
}

/**
 * @description Formats a banking-role user's data scope as a label — a single branch or a region.
 * @param {TFunction} t - The translate function.
 * @param {BankingScope} scope - The role's data lock.
 * @returns {string} The scope's display label.
 */
export function formatBankingScope(t: TFunction, scope: BankingScope): string {
  return scope.type === 'branch'
    ? t('profile.scopeBranch', { branchId: scope.branchId })
    : t('profile.scopeRegion', { location: scope.location })
}
